/**
 * Indicazioni per il magazzino sull'offerta (preventivo).
 *
 * Ogni organizzazione decide in Impostazioni → Offerte quali campi mostrare e
 * quali sono obbligatori (`settings.deal_warehouse_fields`). Colonna vuota =
 * funzione spenta: il form offerta resta com'era. Nata per Say Farm.
 *
 * I valori stanno in `deals.warehouse_info` (jsonb); l'indirizzo di consegna
 * resta `deals.shipping_location_id`, gia' ripreso dall'ordine generato.
 */

export type WarehouseFieldMode = 'off' | 'optional' | 'required';

export type WarehouseListFieldKey = 'packaging_type' | 'payment_status' | 'urgency';
export type WarehouseFieldKey = WarehouseListFieldKey | 'warehouse_notes' | 'shipping_address';

export interface WarehouseFieldsConfig {
  packaging_type: { mode: WarehouseFieldMode; options: string[] };
  payment_status: { mode: WarehouseFieldMode; options: string[] };
  urgency: { mode: WarehouseFieldMode; options: string[] };
  warehouse_notes: { mode: WarehouseFieldMode };
  shipping_address: { mode: WarehouseFieldMode };
}

/** Valori salvati sull'offerta (deals.warehouse_info). */
export interface DealWarehouseInfo {
  packaging_type?: string | null;
  payment_status?: string | null;
  urgency?: string | null;
  warehouse_notes?: string | null;
}

export const WAREHOUSE_FIELD_LABELS: Record<WarehouseFieldKey, string> = {
  packaging_type: 'Tipo di packaging',
  payment_status: 'Stato del pagamento',
  urgency: 'Urgenza',
  warehouse_notes: 'Note per il magazzino',
  // Stesso nome del selettore nell'offerta («Indirizzo di consegna»)
  shipping_address: 'Indirizzo di consegna',
};

export const WAREHOUSE_FIELD_ORDER: WarehouseFieldKey[] = [
  'packaging_type',
  'payment_status',
  'urgency',
  'warehouse_notes',
  'shipping_address',
];

export const WAREHOUSE_LIST_FIELDS: WarehouseListFieldKey[] = ['packaging_type', 'payment_status', 'urgency'];

export const DEFAULT_WAREHOUSE_OPTIONS: Record<WarehouseListFieldKey, string[]> = {
  packaging_type: ['Sacco', 'Cartone', 'Bancale'],
  payment_status: ['In attesa di pagamento', 'Pagato', 'Pagamento alla consegna'],
  urgency: ['Normale', 'Urgente'],
};

const MODES: WarehouseFieldMode[] = ['off', 'optional', 'required'];

function modeOf(raw: unknown): WarehouseFieldMode {
  return MODES.includes(raw as WarehouseFieldMode) ? (raw as WarehouseFieldMode) : 'off';
}

function optionsOf(raw: unknown, fallback: string[]): string[] {
  if (!Array.isArray(raw)) return [...fallback];
  const clean = raw.map(v => (typeof v === 'string' ? v.trim() : '')).filter(Boolean);
  return [...new Set(clean)];
}

/** Config completa a partire dal valore in DB (anche null o parziale). */
export function normalizeWarehouseConfig(raw: unknown): WarehouseFieldsConfig {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, any>;
  return {
    packaging_type: {
      mode: modeOf(r.packaging_type?.mode),
      options: optionsOf(r.packaging_type?.options, DEFAULT_WAREHOUSE_OPTIONS.packaging_type),
    },
    payment_status: {
      mode: modeOf(r.payment_status?.mode),
      options: optionsOf(r.payment_status?.options, DEFAULT_WAREHOUSE_OPTIONS.payment_status),
    },
    urgency: {
      mode: modeOf(r.urgency?.mode),
      options: optionsOf(r.urgency?.options, DEFAULT_WAREHOUSE_OPTIONS.urgency),
    },
    warehouse_notes: { mode: modeOf(r.warehouse_notes?.mode) },
    shipping_address: { mode: modeOf(r.shipping_address?.mode) },
  };
}

/** Almeno un campo attivo: la sezione compare nel form offerta. */
export function isWarehouseConfigActive(config: WarehouseFieldsConfig): boolean {
  return WAREHOUSE_FIELD_ORDER.some(k => config[k].mode !== 'off');
}

/** Campi del form (escluso l'indirizzo, che ha il suo selettore) da mostrare. */
export function visibleInfoFields(config: WarehouseFieldsConfig): Array<WarehouseListFieldKey | 'warehouse_notes'> {
  return (['packaging_type', 'payment_status', 'urgency', 'warehouse_notes'] as const).filter(
    k => config[k].mode !== 'off',
  );
}

/**
 * Etichette dei campi obbligatori mancanti.
 * `hasShippingAddress`: l'offerta ha un indirizzo di consegna — uno scelto tra
 * quelli del cliente oppure la sede del cliente, se compilata.
 */
export function missingWarehouseFields(
  config: WarehouseFieldsConfig,
  info: DealWarehouseInfo | null | undefined,
  hasShippingAddress: boolean,
): string[] {
  const missing: string[] = [];
  const v = info ?? {};
  (['packaging_type', 'payment_status', 'urgency', 'warehouse_notes'] as const).forEach(k => {
    if (config[k].mode === 'required' && !String(v[k] ?? '').trim()) missing.push(WAREHOUSE_FIELD_LABELS[k]);
  });
  if (config.shipping_address.mode === 'required' && !hasShippingAddress) {
    missing.push(WAREHOUSE_FIELD_LABELS.shipping_address);
  }
  return missing;
}

/** Ripulisce i valori prima del salvataggio: stringhe vuote → chiave assente. */
export function cleanWarehouseInfo(info: DealWarehouseInfo | null | undefined): DealWarehouseInfo | null {
  const out: DealWarehouseInfo = {};
  (['packaging_type', 'payment_status', 'urgency', 'warehouse_notes'] as const).forEach(k => {
    const s = String(info?.[k] ?? '').trim();
    if (s) out[k] = s;
  });
  return Object.keys(out).length ? out : null;
}
